import { AlertSquare, Check, Copy01, QrCode01 } from '@untitledui/icons'
import JsBarcode from 'jsbarcode'
import ms from 'ms'
import { useState } from 'react'
import { usePress } from 'react-aria'

import { ProductCard } from '@components/ProductCard'
import Dialog from '@components/designSystem/Dialog/Dialog'
import useCopyToClipboard from '@hooks/useCopyToClipboard'
import useTypedTranslation from '@hooks/useTypedTranslation'

import type { BaseDialogProps } from '@components/designSystem/Dialog/DialogContext'
import type { ProductWrapper } from '@state/product'

interface ProductBarcodeViewProps extends BaseDialogProps {
    product: ProductWrapper
}

const iconPerState = { none: Copy01, success: Check, error: AlertSquare }

function ProductBarcodeView({ product, onClose }: ProductBarcodeViewProps) {
  const { t } = useTypedTranslation()
  const copyToClipboard = useCopyToClipboard()
  const [copyState, setCopyState] = useState<'none' | 'success' | 'error'>('none')

  async function handleCopyButtonClick() {
    const copySuccess = await copyToClipboard(product.gtin)
    setCopyState(copySuccess ? 'success' : 'error')
    setTimeout(() => setCopyState('none'), ms('3s'))
  }

  const { pressProps } = usePress({ onPress: handleCopyButtonClick })

  function renderBarcode(instance: SVGSVGElement | null) {
    if (!instance) return
    JsBarcode(instance, product.gtin, { displayValue: true, height: 200 })
  }

  const Icon = iconPerState[copyState]

  return (
    <Dialog headerIcon={QrCode01} title={t('common.productBarcodeView.title')} description={t('common.productBarcodeView.description')} onClose={onClose}>
      <div className='flex flex-col items-center gap-10' data-testid='product-barcode-dialog'>
        <ProductCard product={product} />
        <svg ref={renderBarcode} aria-label='barcode' />
        <div className='w-full'>
          <p className='mb-1 text-sm font-medium text-secondary'>{t('common.productBarcodeView.copyLabel')}</p>
          <div className='flex w-full items-center gap-2'>
            <div className='flex h-11 w-full items-center rounded-md px-3 py-2 text-md shadow-xs-skeumorphic'>
              <p>{product.gtin}</p>
            </div>
            <button className={`
              flex size-11 cursor-pointer items-center justify-center rounded-xl text-secondary
              hover:bg-bg-tertiary-subtle
            `}
            aria-label='copy-gtin'
            {...pressProps}
            >
              <Icon size={20} />
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  )
}

export default ProductBarcodeView